import React, { useEffect, useRef, useState } from 'react';
import { DiscoveryService } from '../DiscoveryService';
import { Button, Card, Table } from 'react-bootstrap';
import { Pagination } from '../../../shared/components/Pagination';
import { DiscoveryOverview } from '../../../model/discovery/DiscoveryOverview';
import { DiscoveryFilter } from './DiscoveryFilter';
import ModalDiscoveryTokens from './ModalDiscoveryTokens';
import { ErrorToast } from '../../../utils/toasters';
import { TableSkeletonRows } from '../../../shared/components/TableSkeleton';

export function Discovery() {
    const [discovery, setDiscovery] = useState<DiscoveryOverview[]>([]);
    const [expandedRows, setExpandedRows] = useState<number[]>([]);
    const [totalPages, setTotalPages] = useState<number>(0);
    const [change, setChange] = useState<boolean>(false);
    const [state, setState] = useState({
        page: 1,
        perPage: 15,
        displayName: undefined,
        from: undefined,
        to: undefined,
    });
    const [modalData, setModalData] = useState({
        showModal: false,
        company: undefined,
    });
    const hasAnyUsers = discovery.some(item => item.users && item.users.length > 0);

    const [isLoading, setIsLoading] = useState(true);
    const lastQueryKeyRef = useRef<string | null>(null);

    useEffect(() => {
        let isCurrent = true;
        const queryKey = JSON.stringify(state);
        const isNewQuery = lastQueryKeyRef.current !== queryKey;
        lastQueryKeyRef.current = queryKey;

        if (isNewQuery) setIsLoading(true);

        DiscoveryService.getDiscoveryOverview(state)
            .then(res => {
                if (!isCurrent) return;
                setDiscovery(res.data);
                setTotalPages(Math.ceil(res.count / state.perPage));
            })
            .catch(err => {
                if (isCurrent) ErrorToast(err);
            })
            .finally(() => {
                if (isCurrent) setIsLoading(false);
            });

        return () => {
            isCurrent = false;
        };
    }, [change]);

    function handleClick() {
        setChange(!change);
        setExpandedRows([]);
        setState({
            ...state,
            page: 1,
        });
    }

    const handlePages = (updatePage: number) => {
        setState({ ...state, page: updatePage });
        setExpandedRows([]);
        setChange(!change);
    };

    const handleChange = (evt: any) => {
        const value = evt?.target?.value ? evt.target.value : undefined;
        setState({
            ...state,
            [evt.target.name]: value,
        });
    };

    const toggleRow = (companyId: number) => {
        setExpandedRows(prev =>
            prev.includes(companyId) ? prev.filter(id => id !== companyId) : [...prev, companyId]
        );
    };

    return (
        <>
            <ModalDiscoveryTokens
                modalData={modalData}
                setModalData={setModalData}
                setChange={setChange}
                change={change}
            />
            <Card
                className="mb-5 p-4"
                onKeyDown={e => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        handleClick();
                    }
                }}
            >
                <DiscoveryFilter
                    handleClicks={handleClick}
                    handleChanges={handleChange}
                    state={state}
                    setState={setState}
                />
            </Card>
            <Card className="mb-5 p-4">
                <Table hover responsive>
                    <thead>
                        <tr>
                            <th>Company name</th>
                            <th>Searches completed</th>
                            <th className="text-break">Tokens used for searches</th>
                            <th className="text-break">Modash (Approx.)</th>
                            <th className="text-break">Currently available search tokens</th>
                            <th>Profile views</th>
                            <th className="text-break">Tokens used for profile views</th>
                            <th className="text-break">Modash (Approx.)</th>
                            <th className="text-break">Currently available profile tokens</th>
                            {hasAnyUsers && <th>Users</th>}
                            <th>Tokens</th>
                        </tr>
                    </thead>
                    <tbody aria-busy={isLoading}>
                        {isLoading ? (
                            <TableSkeletonRows columns={hasAnyUsers ? 11 : 10} rows={state.perPage} />
                        ) : (
                            discovery.map(e => (
                                <React.Fragment key={e?.companyId}>
                                    <tr>
                                        <td className="text-break">
                                            <span className="text-primary">({e.companyId})</span> {e.companyName}
                                        </td>
                                        <td>
                                            {e.totalTokensSearch > 0
                                                ? e.totalTokensSearch / e.finalDiscoverySearchPrice
                                                : 0}
                                        </td>
                                        <td>{e.totalTokensSearch}</td>
                                        <td>
                                            {Math.round(
                                                (e.totalTokensSearch > 0
                                                    ? e.totalTokensSearch / e.finalDiscoverySearchPrice
                                                    : 0) * 0.15
                                            )}
                                        </td>
                                        <td>{e.discoverySearchTokens}</td>
                                        <td>
                                            {e.totalTokensProfile > 0
                                                ? e.totalTokensProfile / e.finalDiscoveryProfilePrice
                                                : 0}
                                        </td>
                                        <td>{e.totalTokensProfile}</td>
                                        <td>
                                            {e.totalTokensProfile > 0
                                                ? e.totalTokensProfile / e.finalDiscoveryProfilePrice
                                                : 0}
                                        </td>
                                        <td>{e.discoveryTokens}</td>
                                        {hasAnyUsers && (
                                            <td>
                                                {e.users.length > 0 && (
                                                    <Button
                                                        variant="outline-primary"
                                                        onClick={() => toggleRow(e.companyId)}
                                                    >
                                                        {expandedRows.includes(e.companyId) ? 'Hide' : 'Show'}
                                                    </Button>
                                                )}
                                            </td>
                                        )}
                                        <td>
                                            <Button
                                                variant="outline-primary"
                                                onClick={() =>
                                                    setModalData((prev: any) => ({
                                                        ...prev,
                                                        showModal: true,
                                                        company: e,
                                                    }))
                                                }
                                            >
                                                Edit
                                            </Button>
                                        </td>
                                    </tr>
                                    {expandedRows.includes(e.companyId) && e.users && e.users.length > 0 && (
                                        <>
                                            <tr className="table-secondary">
                                                <th>Username</th>
                                                <th>Searches completed</th>
                                                <th>Tokens used for search</th>
                                                <th>Modash (Approx.)</th>
                                                <th>Campaign name</th>
                                                <th>Profile views</th>
                                                <th>Tokens used for profile view</th>
                                                <th>Modash (Approx.)</th>
                                                <th></th>
                                                <th></th>
                                                <th></th>
                                            </tr>
                                            {e.users.map(user => (
                                                <tr key={user?.userId} className="bg-light">
                                                    <td>
                                                        <span className="text-primary">({user?.userId})</span>{' '}
                                                        {user?.firstName ?? '-'} {user?.lastName}
                                                    </td>
                                                    <td>
                                                        {user.totalSearchAmount > 0
                                                            ? user.totalSearchAmount / e.finalDiscoverySearchPrice
                                                            : 0}
                                                    </td>
                                                    <td>{user.totalSearchAmount}</td>
                                                    <td>
                                                        {Math.round(
                                                            (user.totalSearchAmount > 0
                                                                ? user.totalSearchAmount / e.finalDiscoverySearchPrice
                                                                : 0) * 0.15
                                                        )}
                                                    </td>
                                                    <td>
                                                        {user.campaignTitle ? (
                                                            <>
                                                                <span className="text-primary">
                                                                    ({user.campaignId})
                                                                </span>{' '}
                                                                <span>{user.campaignTitle}</span>
                                                            </>
                                                        ) : (
                                                            <span className="text-primary">GLOBAL</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {user.totalProfileAmount > 0
                                                            ? user.totalProfileAmount / e.finalDiscoveryProfilePrice
                                                            : 0}
                                                    </td>
                                                    <td>{user.totalProfileAmount}</td>
                                                    <td>
                                                        {user.totalProfileAmount > 0
                                                            ? user.totalProfileAmount / e.finalDiscoveryProfilePrice
                                                            : 0}
                                                    </td>
                                                    <td></td>
                                                    <td></td>
                                                    <td></td>
                                                </tr>
                                            ))}
                                        </>
                                    )}
                                </React.Fragment>
                            ))
                        )}
                    </tbody>
                </Table>

                <Pagination page={state.page} totalPages={totalPages} handlePagination={handlePages} />
            </Card>
        </>
    );
}
